import { useState, useEffect } from 'react';
import { Bell, Send, Users, UserCheck, Crown, Search, AlertCircle, Link2, Trash2, Plus, Check, X, Lock } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { devError } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';


interface AdminPushNotificationsProps {
  accessToken: string;
}

interface WhitelistedDomain {
  id: string;
  domain: string;
  added_by: string;
  created_at: string;
  notes: string | null;
}

interface SearchedUser {
  id: string;
  name: string;
  email: string;
  userType: 'talent' | 'employer';
  subscription: string;
}

export function AdminPushNotifications({ accessToken }: AdminPushNotificationsProps) {
  const adminModalZ = useAdminModalZIndex();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  
  // ✅ NEW: Target mode - 'bulk' or 'single'
  const [targetMode, setTargetMode] = useState<'bulk' | 'single'>('bulk');
  const [targetType, setTargetType] = useState<'all' | 'talent' | 'employer' | 'plus' | 'employer_messaging' | 'employer_plus' | 'free'>('all');
  
  // ✅ NEW: Single user search
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<SearchedUser | null>(null);
  const [searching, setSearching] = useState(false);
  
  // Navigation type: 'destination' or 'customUrl'
  const [navType, setNavType] = useState<'destination' | 'customUrl'>('destination');
  const [destination, setDestination] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [urlValidation, setUrlValidation] = useState<{ valid: boolean; message: string } | null>(null);
  
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState<{
    matchingUsers: number;
    usersWithPushSubscription: number;
    usersWithPushEnabled: number;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  
  // Whitelist management
  const [whitelistedDomains, setWhitelistedDomains] = useState<WhitelistedDomain[]>([]);
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newDomainNotes, setNewDomainNotes] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  
  // Confirm dialog state
  const [showSendConfirm, setShowSendConfirm] = useState(false);

  // Fetch whitelist on mount
  useEffect(() => {
    fetchWhitelist();
  }, []);

  // ✅ NEW: Search users when query changes
  useEffect(() => {
    if (targetMode === 'single' && userSearchQuery.length >= 2) {
      const debounceTimer = setTimeout(() => {
        searchUsers(userSearchQuery);
      }, 300); // Debounce 300ms
      
      return () => clearTimeout(debounceTimer);
    } else {
      setSearchResults([]);
    }
  }, [userSearchQuery, targetMode]);

  // Validate custom URL in real-time
  useEffect(() => {
    if (navType === 'customUrl' && customUrl) {
      validateCustomUrl(customUrl);
    } else {
      setUrlValidation(null);
    }
  }, [customUrl, navType, whitelistedDomains]);

  const fetchWhitelist = async () => {
    setLoadingWhitelist(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/url-whitelist`,
        {
          headers: {},
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWhitelistedDomains(data.domains || []);
      } else {
        toast.error('Kunne ikke hente whitelist');
      }
    } catch (error) {
      devError('Error fetching whitelist:', error);
      toast.error('Feil ved henting av whitelist');
    } finally {
      setLoadingWhitelist(false);
    }
  };

  const validateCustomUrl = (url: string) => {
    try {
      // Auto-add https:// if protocol is missing
      let normalizedUrl = url.trim();
      if (!normalizedUrl.match(/^https?:\/\//i)) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      
      const parsedUrl = new URL(normalizedUrl);
      const domain = parsedUrl.hostname;
      
      const isWhitelisted = whitelistedDomains.some(d => d.domain === domain);
      
      if (isWhitelisted) {
        setUrlValidation({ 
          valid: true, 
          message: `✅ Domenet "${domain}" er godkjent${url.trim() !== normalizedUrl ? ' (https:// ble lagt til automatisk)' : ''}` 
        });
      } else {
        setUrlValidation({ 
          valid: false, 
          message: `❌ Domenet "${domain}" er ikke godkjent. Legg til i whitelist nedenfor.` 
        });
      }
    } catch (e) {
      setUrlValidation({ 
        valid: false, 
        message: '❌ Ugyldig URL-format. Prøv f.eks. "youtube.com/watch?v=abc" eller "talentfunn.no"' 
      });
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Domenenavn er påkrevd');
      return;
    }

    setAddingDomain(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/url-whitelist`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            domain: newDomain,
            notes: newDomainNotes.trim() || null
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(`Domenet "${data.domain.domain}" ble lagt til!`);
        setNewDomain('');
        setNewDomainNotes('');
        fetchWhitelist(); // Refresh list
      } else {
        toast.error(data.error || 'Kunne ikke legge til domene');
      }
    } catch (error) {
      devError('Error adding domain:', error);
      toast.error('Feil ved adding av domene');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/url-whitelist/${encodeURIComponent(domain)}`,
        {
          method: 'DELETE',
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(`Domenet "${domain}" ble fjernet`);
        fetchWhitelist(); // Refresh list
      } else {
        toast.error(data.error || 'Kunne ikke fjerne domene');
      }
    } catch (error) {
      devError('Error removing domain:', error);
      toast.error('Feil ved fjerning av domene');
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const filters: any = {};
      
      if (targetType === 'talent') filters.user_type = 'talent';
      if (targetType === 'employer') filters.user_type = 'employer';
      if (targetType === 'plus') filters.subscription = 'plus';
      if (targetType === 'employer_messaging') filters.subscription = 'employer';
      if (targetType === 'employer_plus') filters.subscription = 'employer_plus';
      if (targetType === 'free') filters.subscription = 'free';

      const response = await fetchWithAuth(
        `${API_BASE}/admin/preview-push-targets`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ filters }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreview({
          matchingUsers: data.matchingUsers || 0,
          usersWithPushSubscription: data.usersWithPushSubscription || 0,
          usersWithPushEnabled: data.usersWithPushEnabled || 0,
        });
      } else {
        toast.error('Kunne ikke hente forhåndsvisning');
      }
    } catch (error) {
      devError('Error previewing users:', error);
      toast.error('Feil ved forhåndsvisning');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Både tittel og melding er påkrevd');
      return;
    }

    // ✅ NEW: Validate single user selection
    if (targetMode === 'single' && !selectedUser) {
      toast.error('Du må velge en bruker fra søkeresultatene');
      return;
    }

    // Validate custom URL if selected
    if (navType === 'customUrl') {
      if (!customUrl.trim()) {
        toast.error('Custom URL er påkrevd når du velger denne navigasjonstypen');
        return;
      }
      if (!urlValidation?.valid) {
        toast.error('Custom URL må være godkjent i whitelist før sending');
        return;
      }
    }

    setShowSendConfirm(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const payload: any = {
        subject: title,
        message,
      };

      // ✅ NEW: Single user mode
      if (targetMode === 'single' && selectedUser) {
        payload.user_id = selectedUser.id;
      } else {
        // Bulk mode with filters
        const filters: any = {};
        
        if (targetType === 'talent') filters.user_type = 'talent';
        if (targetType === 'employer') filters.user_type = 'employer';
        if (targetType === 'plus') filters.subscription = 'plus';
        if (targetType === 'employer_messaging') filters.subscription = 'employer';
        if (targetType === 'employer_plus') filters.subscription = 'employer_plus';
        if (targetType === 'free') filters.subscription = 'free';
        
        payload.filters = filters;
      }

      // Add navigation based on type
      if (navType === 'destination' && destination) {
        payload.destination = destination;
      } else if (navType === 'customUrl' && customUrl) {
        // Normalize URL: add https:// if missing
        let normalizedUrl = customUrl.trim();
        if (!normalizedUrl.match(/^https?:\/\//i)) {
          normalizedUrl = 'https://' + normalizedUrl;
        }
        payload.custom_url = normalizedUrl;
      }

      const response = await fetchWithAuth(
        `${API_BASE}/admin/send-bulk-push`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (targetMode === 'single') {
          toast.success(`Push-varsling sendt til ${selectedUser?.name}!`);
        } else {
          toast.success(`Push-varsling sendt til ${data.pushSent} av ${data.count} brukere!`);
        }
        setTitle('');
        setMessage('');
        setCustomUrl('');
        setDestination('');
        setPreview(null);
        setUrlValidation(null);
        setSelectedUser(null);
        setUserSearchQuery('');
      } else {
        toast.error(data.error || 'Kunne ikke sende push-varsling');
      }
    } catch (error) {
      devError('Error sending push:', error);
      toast.error('Feil ved sending av push-varsling');
    } finally {
      setSending(false);
      setShowSendConfirm(false);
    }
  };

  // ✅ NEW: Search users
  const searchUsers = async (query: string) => {
    setSearching(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/search-users?q=${encodeURIComponent(query)}`,
        {}
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.users || []);
      } else {
        toast.error('Kunne ikke søke etter brukere');
      }
    } catch (error) {
      devError('Error searching users:', error);
      toast.error('Feil ved søking etter brukere');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
        <div className="flex items-center gap-3 mb-2">
          <Bell className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl text-gray-800">Send Push-varsling</h2>
        </div>
        <p className="text-gray-600 text-sm">
          Send push-notifikasjoner til brukere basert på type eller abonnement. 
          Kan navigere til interne sider eller eksterne URLer.
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 space-y-4">
        {/* ✅ NEW: Target Mode Selection */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">Send til</label>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => {
                setTargetMode('bulk');
                setSelectedUser(null);
                setUserSearchQuery('');
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetMode === 'bulk'
                  ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>Flere brukere (bulk)</span>
            </button>
            <button
              onClick={() => {
                setTargetMode('single');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetMode === 'single'
                  ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              <Search className="w-5 h-5" />
              <span>Enkeltbruker</span>
            </button>
          </div>
        </div>

        {/* ✅ NEW: Single User Search */}
        {targetMode === 'single' ? (
          <div>
            <label className="block text-gray-700 mb-2 font-medium">Søk etter bruker</label>
            <input
              type="text"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              placeholder="Søk på navn..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            
            {/* Search Results */}
            {searching && (
              <div className="mt-3 flex items-center justify-center py-4">
                <Loader size="md" className="text-purple-600" />
                <span className="ml-2 text-gray-600">Søker...</span>
              </div>
            )}
            
            {!searching && searchResults.length > 0 && (
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => {
                      setSelectedUser(user);
                      setUserSearchQuery(user.name);
                      setSearchResults([]);
                    }}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                      selectedUser?.id === user.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.userType === 'talent'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {user.userType === 'talent' ? 'Talent' : 'Arbeidsgiver'}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                          {user.subscription || 'free'}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {!searching && userSearchQuery.length >= 2 && searchResults.length === 0 && (
              <div className="mt-3 p-4 bg-gray-50 rounded-xl text-center text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Ingen brukere funnet for "{userSearchQuery}"</p>
              </div>
            )}
            
            {userSearchQuery.length > 0 && userSearchQuery.length < 2 && (
              <p className="mt-2 text-xs text-gray-500">Skriv minst 2 tegn for å søke</p>
            )}
            
            {/* Selected User Confirmation */}
            {selectedUser && (
              <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-900 font-medium">Valgt bruker:</p>
                    <p className="text-green-700">{selectedUser.name} ({selectedUser.email})</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Bulk Target Selection */
          <div>
            <label className="block text-gray-700 mb-2 font-medium">Målgruppe</label>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
            <button
              onClick={() => {
                setTargetType('all');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetType === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Alle</span>
            </button>
            <button
              onClick={() => {
                setTargetType('talent');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetType === 'talent'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserCheck className="w-4 h-4" />
              <span>Talent</span>
            </button>
            <button
              onClick={() => {
                setTargetType('employer');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetType === 'employer'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Crown className="w-4 h-4" />
              <span>Arbeidsgiver</span>
            </button>
            <button
              onClick={() => {
                setTargetType('free');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetType === 'free'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Gratis</span>
            </button>
            <button
              onClick={() => {
                setTargetType('plus');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetType === 'plus'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Crown className="w-4 h-4" />
              <span className="text-sm">Talentfunn Pluss</span>
            </button>
            <button
              onClick={() => {
                setTargetType('employer_messaging');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetType === 'employer_messaging'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Crown className="w-4 h-4" />
              <span className="text-sm">Arbeidsgiver Basis</span>
            </button>
            <button
              onClick={() => {
                setTargetType('employer_plus');
                setPreview(null);
              }}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                targetType === 'employer_plus'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Crown className="w-4 h-4" />
              <span className="text-sm">Arbeidsgiver Pluss</span>
            </button>
          </div>
        </div>
        )}

        {/* Title */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">Tittel</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="F.eks. 'Ny funksjon tilgjengelig!'"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            maxLength={100}
          />
          <p className="text-sm text-gray-500 mt-1">{title.length}/100 tegn</p>
        </div>

        {/* Message */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">Melding</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Skriv meldingen som skal sendes til brukerne..."
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px]"
            maxLength={500}
          />
          <p className="text-sm text-gray-500 mt-1">{message.length}/500 tegn</p>
        </div>

        {/* ✅ NEW: Navigation Type Selection */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">Navigasjon når bruker klikker varslet</label>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              onClick={() => setNavType('destination')}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                navType === 'destination'
                  ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              <span>Intern side</span>
            </button>
            <button
              onClick={() => setNavType('customUrl')}
              className={`px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                navType === 'customUrl'
                  ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              <Link2 className="w-4 h-4" />
              <span>Custom URL</span>
            </button>
          </div>

          {/* Destination dropdown (internal) */}
          {navType === 'destination' && (
            <select
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Ingen (varselet er kun informativt)</option>
              <option value="home">Home</option>
              <option value="messages">Messages</option>
              <option value="profile">Profile</option>
              <option value="subscription">Subscription</option>
              <option value="gigs">Gigs</option>
              <option value="my-gigs">My Gigs</option>
              <option value="my-interests">My Interests</option>
              <option value="gig-interests">Gig Interests</option>
              <option value="favorites">Favorites</option>
              <option value="saved-searches">Saved Searches</option>
              <option value="settings">Settings</option>
              <option value="faq">FAQ</option>
              <option value="about">About</option>
            </select>
          )}

          {/* Custom URL input */}
          {navType === 'customUrl' && (
            <div className="space-y-2">
              <input
                type="url"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=abc123"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              
              {/* Real-time validation feedback */}
              {urlValidation && (
                <div className={`flex items-start gap-2 p-3 rounded-xl ${
                  urlValidation.valid 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {urlValidation.valid ? (
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <p className={`text-sm ${urlValidation.valid ? 'text-green-700' : 'text-red-700'}`}>
                    {urlValidation.message}
                  </p>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-2">
            {navType === 'destination' 
              ? 'Brukeren navigeres til valgt side i appen når de klikker på varselet'
              : 'Custom URL kan være eksterne linker (YouTube, Medium, etc.) eller hash-baserte deep links (#subscription)'
            }
          </p>
        </div>

        {/* Preview Count - Only show in bulk mode */}
        {targetMode === 'bulk' && preview !== null && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Search className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-900 font-medium">
                {preview.usersWithPushEnabled} brukere har push aktivert (registrert)
              </p>
              <p className="text-blue-700 text-sm mt-1">
                Målgruppe: <span className="font-medium capitalize">{targetType === 'all' ? 'Alle brukere' : targetType}</span>
              </p>
              <p className="text-blue-700 text-xs mt-1">
                Matcher filter: {preview.matchingUsers}. Med subscription: {preview.usersWithPushSubscription}. Levering kan fortsatt feile hvis endpoint er utløpt eller push er skrudd av i OS/nettleser.
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {targetMode === 'bulk' && (
            <button
              onClick={handlePreview}
              disabled={previewing}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-medium flex items-center justify-center gap-2"
            >
              {previewing ? (
                <>
                  <Loader size="md" />
                  <span>Laster...</span>
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  <span>Forhåndsvis antall</span>
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim()}
            className={`px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              targetMode === 'single' ? 'w-full' : 'flex-1'
            }`}
          >
            {sending ? (
              <>
                <Loader size="md" />
                <span>Sender...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Send varsling</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ✅ NEW: URL Whitelist Management */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-6 h-6 text-purple-600" />
          <div>
            <h3 className="text-xl font-semibold text-gray-800">Godkjente domener (URL Whitelist)</h3>
            <p className="text-sm text-gray-600">Administrer hvilke eksterne domener som kan brukes i custom URLs</p>
          </div>
        </div>

        {/* Add new domain */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3">
          <label className="block text-gray-700 font-medium">Legg til nytt domene</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="youtube.com"
              className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              onClick={handleAddDomain}
              disabled={addingDomain || !newDomain.trim()}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addingDomain ? (
                <Loader size="sm" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              <span>Legg til</span>
            </button>
          </div>
          <input
            type="text"
            value={newDomainNotes}
            onChange={(e) => setNewDomainNotes(e.target.value)}
            placeholder="Notater (valgfritt, f.eks. 'YouTube tutorials')"
            className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
          />
          <p className="text-xs text-gray-500">
            💡 Skriv kun domenet (f.eks. "youtube.com"), ikke full URL. Protokoll og www fjernes automatisk.
          </p>
        </div>

        {/* Domain list */}
        <div>
          <label className="block text-gray-700 font-medium mb-3">Godkjente domener</label>
          {loadingWhitelist ? (
            <div className="flex items-center justify-center py-8">
              <Loader size="lg" className="text-purple-600" />
            </div>
          ) : whitelistedDomains.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Lock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Ingen domener godkjent ennå</p>
            </div>
          ) : (
            <div className="space-y-2">
              {whitelistedDomains.map((domain) => (
                <div
                  key={domain.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{domain.domain}</span>
                      {domain.added_by === 'system' && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          System
                        </span>
                      )}
                    </div>
                    {domain.notes && (
                      <p className="text-sm text-gray-500 mt-0.5">{domain.notes}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Lagt til av {domain.added_by} • {new Date(domain.created_at).toLocaleDateString('nb-NO')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveDomain(domain.domain)}
                    disabled={domain.added_by === 'system'}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={domain.added_by === 'system' ? 'System-domener kan ikke slettes' : 'Fjern domene'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <h3 className="text-gray-800 font-medium mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-purple-600" />
          Viktig informasjon
        </h3>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-purple-600 mt-0.5">•</span>
            <span>Push-varsler krever at brukeren har aktivert push i innstillinger</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-600 mt-0.5">•</span>
            <span>Custom URLs må være fra godkjente domener for sikkerhet</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-600 mt-0.5">•</span>
            <span>System-domener (talentfunn.no, youtube.com, youtu.be) er forhåndsgodkjent og kan ikke slettes</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-600 mt-0.5">•</span>
            <span>Brukere som har skrudd av "Systemmeldinger" i innstillinger får IKKE push-varsler</span>
          </li>
        </ul>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showSendConfirm}
        onClose={() => setShowSendConfirm(false)}
        onConfirm={confirmSend}
        title="Bekreft sending av push-varsling"
        message={
          targetMode === 'single' && selectedUser
            ? `Er du sikker på at du vil sende push-varsling til ${selectedUser.name}?`
            : `Er du sikker på at du vil sende push-varsling til ${preview?.usersWithPushEnabled ?? 'valgt målgruppe'}?`
        }
        confirmText="Send"
        cancelText="Avbryt"
        variant="warning"
        loading={sending}
        loadingText="Sender..."
        portalZIndex={adminModalZ}
      />
    </div>
  );
}
